import { useEffect, useState } from 'react';
import { directusAPI } from '../../../api';
import { isSuperAdminUser } from './adminRoles';

/**
 * Super admin detection, resolved once per signed-in session.
 *
 * Directus 11 grants rights through policies: `GET /policies/me/globals`
 * returns `{ app_access, admin_access }` for the current user and
 * `admin_access === true` means super admin. If that endpoint fails (older
 * Directus, network), the fallback is `GET /users/me?fields=id,role` checked
 * against ADMIN_ROLE_IDS / ADMIN_USER_IDS. `/users/me` is requested in the
 * same round anyway, because the review form needs the user id for
 * `reviewed_by`.
 *
 * The result lives in a module-level cache shared by every component, so
 * renders and remounts do not repeat the requests. A failed resolution is not
 * cached (the next mount retries). Call clearSuperAdminCache() on sign-out.
 */

const EMPTY_RESULT = Object.freeze({ isSuperAdmin: false, userId: null });

let cachedResult = null;
let pendingPromise = null;
let cacheGeneration = 0;

const readData = (settled) => (settled.status === 'fulfilled' ? settled.value?.data?.data ?? null : null);

export const resolveSuperAdmin = () => {
  if (cachedResult) return Promise.resolve(cachedResult);
  if (pendingPromise) return pendingPromise;

  const generation = cacheGeneration;
  pendingPromise = Promise.allSettled([
    directusAPI.get('/policies/me/globals'),
    directusAPI.get('/users/me', { params: { fields: 'id,role' } })
  ]).then(([globalsResult, userResult]) => {
    const globals = readData(globalsResult);
    const user = readData(userResult);
    const userId = user?.id ? String(user.id) : null;

    let result;
    if (globals && typeof globals.admin_access === 'boolean') {
      result = { isSuperAdmin: globals.admin_access === true, userId };
    } else if (user) {
      result = { isSuperAdmin: isSuperAdminUser(user), userId };
    } else {
      // Both requests failed: answer "not an admin" without caching it.
      if (generation === cacheGeneration) pendingPromise = null;
      return EMPTY_RESULT;
    }

    if (generation === cacheGeneration) {
      cachedResult = result;
      pendingPromise = null;
    }
    return result;
  }).catch(() => {
    if (generation === cacheGeneration) pendingPromise = null;
    return EMPTY_RESULT;
  });

  return pendingPromise;
};

export const clearSuperAdminCache = () => {
  cacheGeneration += 1;
  cachedResult = null;
  pendingPromise = null;
};

export const useIsSuperAdmin = () => {
  const [state, setState] = useState(() => (
    cachedResult
      ? { ...cachedResult, loading: false }
      : { ...EMPTY_RESULT, loading: true }
  ));

  useEffect(() => {
    if (cachedResult) {
      setState((prev) => (
        prev.loading || prev.isSuperAdmin !== cachedResult.isSuperAdmin || prev.userId !== cachedResult.userId
          ? { ...cachedResult, loading: false }
          : prev
      ));
      return undefined;
    }

    let active = true;
    resolveSuperAdmin().then((result) => {
      if (active) setState({ ...result, loading: false });
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
};

export default useIsSuperAdmin;
